import { Injectable } from '@angular/core';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';

@Injectable({
  providedIn: 'root',
})
export class CustomGlobalFilterService extends GlobalFilterService {
  /**
   * Overrides updateFilterSelection to handle 'values' being a string instead of an array.
   * All other methods are inherited from GlobalFilterService.
   */
  override updateFilterSelection(
    keyOrKeys: string | string[],
    values: any[] | any, // Allow non-array inputs
    isQueryParam = false,
    widgetId?: string,
    emit: boolean = true,
    filterListener?: string,
    listenerExtraParams?: { [widgetId: string]: { [key: string]: any } },
  ): void {
    const keys = Array.isArray(keyOrKeys)
      ? keyOrKeys
      : (keyOrKeys || '')
          .split(',')
          .map((k) => k.trim())
          .filter((k) => k);
    let hasMeaningfulChange = false;

    keys.forEach((key) => {
      // CUSTOM CHANGE: Check if values is array. If not, use it directly.
      const valueToStore = Array.isArray(values) ? values?.[0] || null : values;
      const nextStoredValue = this.normalizeFilterValueForCompare(valueToStore);
      const previousStoredValue = this.normalizeFilterValueForCompare(this.selectedFilters[key]);
      const nextFilterValue = this.normalizeFilterValueForCompare(values);
      const previousFilterValue = this.normalizeFilterValueForCompare(
        isQueryParam ? this.filters.queryParams[key] : this.filters.requestParams[key]
      );

      if (!this.areFilterValuesEqual(previousStoredValue, nextStoredValue) ||
          !this.areFilterValuesEqual(previousFilterValue, nextFilterValue)) {
        hasMeaningfulChange = true;
      }

      this.selectedFilters[key] = valueToStore;

      if (isQueryParam) {
        this.filters.queryParams[key] = values;
      } else {
        this.filters.requestParams[key] = values;
      }
    });

    this.widgetId = widgetId || null;
    if (emit && !hasMeaningfulChange) {
      return;
    }

    if (emit) {
      const filterData = {
        ...this.filters,
        widgetId: this.widgetId,
        changedFilterKey: keys.length === 1 ? keys[0] : keys,
        filterListener,
        ...(listenerExtraParams ? { listenerExtraParams } : {}),
      };

      this.filterSubject.next(filterData);
      this.currentSelectedFilter.next({ key: keys });
    }
  }

  private normalizeFilterValueForCompare(value: any): any {
    if (Array.isArray(value)) {
      return value.map((item) => this.normalizeFilterValueForCompare(item));
    }

    if (value && typeof value === 'object') {
      return Object.keys(value)
        .sort()
        .reduce<Record<string, any>>((result, key) => {
          result[key] = this.normalizeFilterValueForCompare(value[key]);
          return result;
        }, {});
    }

    return value ?? null;
  }

  private areFilterValuesEqual(left: any, right: any): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
  }
}
